

import { useState, useEffect, useMemo, useCallback } from "react";
import { useRouter } from "next/navigation";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { IProduct, PopulatedCartItem } from "../types/types";

export const useExpandedCard = (
  product: IProduct,
  cartItemToEdit: PopulatedCartItem | null,
  onClose: () => void
) => {
  const router = useRouter();
  const { addToCart, updateItem, isLoading: isCartLoading } = useCart();
  const { isWholesaleUser, user, openAuthModal } = useAuth();

  const [selectedCustomizations, setSelectedCustomizations] = useState<{
    [key: string]: string;
  }>({});
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isPremiumEnabled, setIsPremiumEnabled] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"details" | "gallery">("details");
  const [quantity, setQuantity] = useState(1);

  const minQuantity = useMemo(() => {
    return isWholesaleUser ? product.wholesaleMinQuantity : (product.retailMinQuantity || 1);
  }, [isWholesaleUser, product.wholesaleMinQuantity, product.retailMinQuantity]);

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= minQuantity) {
      setQuantity(newQuantity);
    }
  };

  const handleSelectionChange = useCallback((type: string, value: string) => {
    setSelectedCustomizations((prev) => ({ ...prev, [type]: value }));
  }, []);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKeyDown);

    const initialSelections: { [key: string]: string } = {};
    const initialQuantity = cartItemToEdit?.quantity || minQuantity;
    setQuantity(initialQuantity);

    if (cartItemToEdit) {
      product.customization.forEach((cust) => {
        const selectedValue = cartItemToEdit.selectedOptions.find((optValue) =>
          cust.options.some((o) => o.value === optValue)
        );
        initialSelections[cust.type] =
          selectedValue || cust.options[0]?.value || "";
      });
      
      const selectedColor = cartItemToEdit.selectedOptions.find(
        (opt) =>
          Array.isArray(product.colors) &&
          product.colors.some((c) => c.name === opt)
      );
      initialSelections["Color"] = selectedColor || product.colors[0].name;
      
      const hasPremium = cartItemToEdit.selectedOptions.some((opt) =>
        opt.includes("Premium")
      );
      setIsPremiumEnabled(hasPremium);
    } else {
      if (product.customization) {
        product.customization.forEach((cust) => {
          if (cust.options?.[0])
            initialSelections[cust.type] = cust.options[0].value;
        });
      }
      
      if (product.colors?.[0]) {
        const firstAvailableColor = product.colors.find((color) => {
          if (!color.availableFor || color.availableFor.length === 0)
            return true;
          return Object.values(initialSelections).some((selectedValue) =>
            color.availableFor.includes(selectedValue)
          );
        });
        initialSelections["Color"] = firstAvailableColor?.name || "";
      }
      setIsPremiumEnabled(false);
    }
    
    setSelectedCustomizations(initialSelections);
    setActiveTab("details");
    
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [product, cartItemToEdit, onClose, minQuantity]);

  const handleConfirm = async () => {
    const options: string[] = [];
    for (const type in selectedCustomizations) {
      if (type !== "Accessories" && selectedCustomizations[type]) {
        options.push(selectedCustomizations[type]);
      }
    }
    if (isPremiumEnabled) {
      const selectedMotor = selectedCustomizations["Motor Type"] || "";
      const premiumOption = selectedMotor.includes("Double")
        ? "Premium (for double motor)"
        : "Premium (for single motor)";
      options.push(premiumOption);
    } else {
      options.push("Standard");
    }
    
    let wasSuccessful = false;
    if (cartItemToEdit) {
      wasSuccessful = await updateItem(cartItemToEdit._id, quantity, options);
    } else {
      wasSuccessful = await addToCart(product._id, quantity, options);
    }
    
    if (wasSuccessful) {
      onClose();
    }
  };

  const handleBuyNow = () => {
    if(!user) {
      openAuthModal();
      return;
    }
    const options: string[] = [];
    for (const type in selectedCustomizations) {
      if (type !== "Accessories" && selectedCustomizations[type]) {
        options.push(selectedCustomizations[type]);
      }
    }
    if (isPremiumEnabled) {
      const selectedMotor = selectedCustomizations["Motor Type"] || "";
      const premiumOption = selectedMotor.includes("Double")
        ? "Premium (for double motor)"
        : "Premium (for single motor)";
      options.push(premiumOption);
    } else {
      options.push("Standard");
    }

    const encodedOptions = encodeURIComponent(options.join(","));
    const checkoutUrl = `/checkout?productId=${product._id}&quantity=${quantity}&options=${encodedOptions}`;
    router.push(checkoutUrl);
  };

  return {
    selectedCustomizations,
    setSelectedCustomizations,
    currentImageIndex,
    setCurrentImageIndex,
    isPremiumEnabled,
    setIsPremiumEnabled,
    activeTab,
    setActiveTab,
    quantity,
    minQuantity,
    handleQuantityChange,
    handleSelectionChange,
    handleConfirm,
    handleBuyNow,
    isCartLoading,
    isWholesaleUser,
  };
};